import { connect } from 'react-redux';
import {
  worklistMapDispatchToProps,
  worklistMapStateToProps
} from './service-desk-component';
import {
  mapUpdatedDispatchToCodeBenchProps,
  mapUpdatedStateToCodeBenchProps
} from '../../views/accelerate/code-bench-component';
import { ServiceDeskAndGradeBench } from './service-desk-and-gradebench-component';
import {
  codeBenchActions,
  gradeBenchPanelActions,
  conditionalUtils
} from '@codametrix/ui-common';
import { spanSelectors } from '@codametrix/ui-common';
import {
  launchWorkitemInAmplify,
  getPrevOrNextCase
} from '../../actions/cases.action';
import {
  getIsAuditor,
  getSubmitButtonConfig,
  getUserRolesFromActiveContext
} from '../../core/cqa/CQASubmitButton';

const { selectSpanAnnotations } = spanSelectors;
const { disabledCapability, enabledCapability } = conditionalUtils;
const stateMapper = (state: CMx.ApplicationState) => {
  const roles = getUserRolesFromActiveContext(state.ui.context);

  const isAuditor = getIsAuditor(
    state.gradeBenchPanel,
    roles,
    state.gradeBenchPanel.workerInfo?.worker_id ?? null
  );

  const associatedWorkitems = state.gradeBenchPanel.associatedWorkitems;

  const currentWorkitemId = state?.gradeBenchPanel?.invoices?.length
    ? state.gradeBenchPanel.invoices[0].sdCaseId
    : '-1';
  const isLastWorkitemInGroup =
    associatedWorkitems.length > 0 &&
    associatedWorkitems.findIndex(
      workitem => workitem.data.case_id === currentWorkitemId
    ) ===
      associatedWorkitems.length - 1;
  const nextVersionButtonConfig = {
    text: 'Next Version',
    enabled: !isLastWorkitemInGroup,
    tooltip: 'Navigate to next version of the work item'
  };

  const actionBarSelectMultipleWorkitems =
    isAuditor && associatedWorkitems.length > 1
      ? enabledCapability
      : disabledCapability;

  const submitButtonConfig = getSubmitButtonConfig(
    state.gradeBenchPanel,
    roles,
    state.gradeBenchPanel.workerInfo?.worker_id ?? null
  );

  const annotatedNotes = state.codeBench.notes.map(note => ({
    ...note,
    spanAnnotations: selectSpanAnnotations(state.gradeBenchPanel)
  }));

  return {
    listProps: worklistMapStateToProps(state),
    detailProps: {
      ...mapUpdatedStateToCodeBenchProps(state),
      activity: state.gradeBenchPanel.activity,
      gradeableInvoices: state.gradeBenchPanel.invoices,
      fieldDefinitions: state.gradeBenchPanel.fieldDefinitions,
      qaRuleCollection: {
        sampleReason: state.gradeBenchPanel.qaRule.data.sample_reason,
        guideline: state.gradeBenchPanel.qaRule.data.guideline,
        project_configuration:
          state.gradeBenchPanel.qaRule.data.project_configuration
      },
      capabilities: {
        ...state.capabilities,
        capabilities: {
          ...state.capabilities.capabilities,
          servicedeskAction: enabledCapability,
          submitCase: enabledCapability,
          actionBarSelectMultipleWorkitems: actionBarSelectMultipleWorkitems
        }
      },
      userContext: state.ui.context.activeContext,
      riskCategories: state.gradeBenchPanel.riskCategories,
      cqaCodeAssessmentRiskDictionaries:
        state.gradeBenchPanel.cqaCodeAssessmentRiskDictionaries,
      gradingErrors: state.gradeBenchPanel.gradingErrors,
      submitButtonConfig: submitButtonConfig,
      nextVersionButtonConfig: nextVersionButtonConfig,
      associatedWorkitems: state.gradeBenchPanel.associatedWorkitems,
      notes: annotatedNotes
    }
  };
};
const dualDispatchMapper = (dispatch: any) => {
  return {
    listMethods: {
      ...worklistMapDispatchToProps(dispatch),
      openAmplifyCase: (launchConfig: CMxCommonApp.LaunchWorkItemConfig) => {
        dispatch(launchWorkitemInAmplify(launchConfig));
        dispatch(codeBenchActions.hideSideNavBar());
      }
    },
    detailMethods: {
      ...mapUpdatedDispatchToCodeBenchProps(dispatch),
      onGradeUpdate: (update: CMxCommonApp.GradeBenchSelectEvent) => {
        dispatch(gradeBenchPanelActions.gradeUpdate(update));
      },
      fetchGradePanelConfigurations: () => {
        dispatch(gradeBenchPanelActions.fetchRiskCategories());
      },
      onCodeSearchSelection: (update: Accelerate.CodeAddActionPayload) => {
        dispatch(gradeBenchPanelActions.addCode(update));
      },
      onGradeRemove: (update: Accelerate.DeleteCQACodeParams) => {
        dispatch(gradeBenchPanelActions.deleteCQACode(update));
      },
      onCodeEvent: (update: CMxCommonApp.GradeBenchCodeEvent) => {
        dispatch(gradeBenchPanelActions.resequenceCode(update));
      },
      fetchQaRule: (update: Accelerate.FetchQaRuleParams) => {
        dispatch(gradeBenchPanelActions.fetchQaRule(update));
      },
      onCursorAction: async (
        cursorAPIPayload: CMxCommonApp.CursorAPIPayload
      ) => {
        dispatch(getPrevOrNextCase(cursorAPIPayload));
      },
      onLoadFromURLMatch: (params: any) => {
        dispatch(gradeBenchPanelActions.loadCQAItemFromURL(params));
      },
      onCreateCQATransaction: (
        update: Accelerate.CreateCQATransactionParams
      ) => {
        dispatch(gradeBenchPanelActions.createCQATransaction(update));
      },
      onDeleteCQATransaction: (
        update: Accelerate.DeleteCQATransactionParams
      ) => {
        dispatch(gradeBenchPanelActions.deleteCQATransaction(update));
      },
      submitCQAWorkitem: (
        cursorAPIPayload: CMxCommonApp.CursorAPIPayload,
        status: string = 'CQA_COMPLETE'
      ) => {
        dispatch(
          gradeBenchPanelActions.submitCQAWorkitem({
            cursorAPIPayload,
            status
          })
        );
      },
      fetchAssociatedWorkItems: (workitemId: number) => {
        dispatch(
          gradeBenchPanelActions.retrieveAssociatedWorkitems(workitemId)
        );
      },
      loadAssociatedWorkItem: (
        cursorAPIContext: CMxCommonApp.CursorAPIContext,
        sdWorkitem: CMxAPI.WorkItem
      ) => {
        dispatch(
          gradeBenchPanelActions.launchAssociatedWorkItemInGradeBench({
            cursorAPIContext,
            sdWorkitem
          })
        );
      },
      fetchWorkerInfoForUser: (userContext: CMxCommonApp.Context) => {
        dispatch(gradeBenchPanelActions.fetchWorkerInfoForUser(userContext));
      },
      fetchGraderWorkItemActivity: (sdCaseId: string) => {
        dispatch(gradeBenchPanelActions.getGraderWorkItemActivity(sdCaseId));
      },
    }
  };
};

export const ConnectedServiceDeskAndGradeBench = connect(
  stateMapper,
  dualDispatchMapper
)(ServiceDeskAndGradeBench);
